/**
 * @file snoozeDates.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-16
 * @modified 2026-09-16
 *
 * The date arithmetic behind snoozing an account on Gone Quiet.
 *
 * Everything here is a Europe/London CALENDAR date as a 'YYYY-MM-DD' string —
 * never a Date at midnight, never a UTC timestamp. A snooze is "until 16 Dec",
 * a day on the office's calendar, and the moment a Date object is involved the
 * answer starts depending on the clock and zone of whichever laptop or phone
 * is asking: 00:30 on a BST morning is still yesterday in UTC.
 *
 * `until` is inclusive — hidden through that day, back the day after — which is
 * how the server reads it (routes/repDashboard.js).
 */

import { formatDateShort } from './dateFormatters';

/** The choices offered in the snooze pop-up, in order. */
export const SNOOZE_OPTIONS = [
  { key: '1m', months: 1, label: '1 month' },
  { key: '3m', months: 3, label: '3 months' },
  { key: '6m', months: 6, label: '6 months' },
  { key: '12m', months: 12, label: '12 months' },
  { key: 'date', label: 'Specific date' },
  { key: 'never', label: 'Never' }
];

/** Today in London as YYYY-MM-DD, whatever zone this device is set to. */
export function londonTodayISO(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(now);
  const get = (type) => parts.find((p) => p.type === type).value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** True for a real YYYY-MM-DD calendar date — 2027-02-29 is not one. */
export function isCalendarDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/**
 * The same day `months` later, held to the end of a shorter month.
 *
 * 31 January + 1 month is 28 February (29th in a leap year), not 3 March —
 * which is where Date#setMonth lands, and nobody who asked for "a month" means
 * that. 29 February + 12 months is 28 February.
 */
export function addMonthsISO(iso, months) {
  if (!isCalendarDate(iso)) throw new Error(`Not a date: ${iso}`);
  const [y, m, d] = iso.split('-').map(Number);
  const total = (m - 1) + months;
  const year = y + Math.floor(total / 12);
  const monthIndex = ((total % 12) + 12) % 12;
  // Day 0 of the following month is the last day of this one.
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const day = Math.min(d, lastDay);
  return `${String(year).padStart(4, '0')}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * What to send as `until` for a chosen option.
 *
 * @param {string} key          an SNOOZE_OPTIONS key
 * @param {string} today        London today, YYYY-MM-DD
 * @param {string} [specific]   the picked date, for key 'date'
 * @returns {string|null}       YYYY-MM-DD, or null for Never
 * @throws  on an unknown key, or a specific date that is malformed or past
 */
export function snoozeUntilFor(key, today, specific) {
  if (key === 'never') return null;
  if (key === 'date') {
    if (!isCalendarDate(specific)) throw new Error('Pick a date');
    // Same-shape ISO strings compare the way the dates do.
    if (specific < today) throw new Error('That date has already passed');
    return specific;
  }
  const option = SNOOZE_OPTIONS.find((o) => o.key === key && o.months);
  if (!option) throw new Error(`Unknown snooze option: ${key}`);
  return addMonthsISO(today, option.months);
}

/** "until 16 Dec 2026", or "never" for a snooze with no end. */
export function formatSnoozeUntil(until) {
  if (until === null || until === undefined) return 'never';
  // T00:00:00 makes it LOCAL midnight, so the formatter's local getters read
  // back the same calendar day in any zone. A bare 'YYYY-MM-DD' parses as UTC.
  return `until ${formatDateShort(`${until}T00:00:00`)}`;
}

/** A YYYY-MM-DD string as a local-midnight Date, for the date picker. */
export function isoToLocalDate(iso) {
  if (!isCalendarDate(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}
